import React, { useState } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CurrencyCode } from '@cash-web/protos/squareup/common/currency_codes.pb'
import { AmountBlocker } from '@cash-web/protos/squareup/franklin/blockers/amount_blocker.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetAmount } from '@cash-web/shared-data-access-api/react-query/useMutationSetAmount'
import { GiantMoneyInput, GiantPercentageInput } from '@cash-web/shared-feature-common'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { numberToScientificNumber, scientificNumberToFloat } from '@cash-web/shared-util-formatting'

import { getSetAmountErrors } from '../../util/statusErrors'
import { BaseBlocker, BaseBlockerFormFields, BlockerProps } from '../BaseBlocker/BaseBlocker'

interface FormFields extends BaseBlockerFormFields {
  amount: string
}
interface Props extends BlockerProps {
  blocker: AmountBlocker
  title?: string
  subtitle?: string
  buttons?: React.ReactNode[]
}

export function BlockerAmount({ blocker, buttons }: Props): React.ReactElement {
  const form = useForm<FormFields>({ mode: 'onTouched' })
  const { t } = useTranslation()
  const flow = useFlowContext()
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const [formattedValue, setFormattedValue] = useState('')

  const isPercentageInput = blocker.percentage_config
  const { mutateAsync: mutateAmount } = useMutationSetAmount({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })

  const onSubmit = async ({ amount }: FormFields) => {
    const numberAmount = parseInt(amount)
    const scientificNumber = numberToScientificNumber(numberAmount)
    const requestBody = isPercentageInput
      ? { percentage_result: { percentage: scientificNumber } }
      : { amount_result: { amount: { amount: numberAmount, currency_code: CurrencyCode.USD } } }

    await handleBlockerResponse({
      promisedBlockerUpdate: mutateAmount(requestBody),
      errors: getSetAmountErrors(t),
      isResponseSuccess: response => {
        const blockerDescriptors = response.response_context?.scenario_plan?.blocker_descriptors
        return !!blockerDescriptors?.length && blockerDescriptors?.length > 0
      },
    })
  }

  const { title, subtitle } = blocker
  let minimumLimit: number | undefined
  let maximumLimit: number | undefined
  if (isPercentageInput) {
    minimumLimit = scientificNumberToFloat(blocker.percentage_config?.minimum_amount)
    maximumLimit = scientificNumberToFloat(blocker.percentage_config?.maximum_amount)
  } else {
    minimumLimit = blocker.money_config?.minimum_amount?.amount
    maximumLimit = blocker.money_config?.maximum_amount?.amount
  }

  if (minimumLimit == null || maximumLimit == null) {
    throw new Error('Missing minimum or maximum limit for BlockerAmount from server')
  }

  return (
    <BaseBlocker mainText={title} secondaryText={subtitle} form={form} onSubmit={onSubmit} buttons={buttons}>
      {isPercentageInput ? (
        <GiantPercentageInput
          formattedValue={formattedValue}
          setFormattedValue={setFormattedValue}
          name="amount"
          minimumLimit={minimumLimit}
          maximumLimit={maximumLimit}
        />
      ) : (
        <GiantMoneyInput
          formattedValue={formattedValue}
          setFormattedValue={setFormattedValue}
          name="amount"
          minimumAmount={minimumLimit}
          maximumAmount={maximumLimit}
        />
      )}
    </BaseBlocker>
  )
}
